import { UserType } from '../application/domain/utils/user.type'
import { User } from '../application/domain/models/entity/user'
import axiosInstance from './axios'
import { AxiosResponse } from 'axios'
import { Organization } from '../application/domain/models/entity/organization'
import { IActionSignup } from '../store/auth/types'

/**
 * User service
 * @class
 * @category Services
 */
class UserService {

    constructor(
        private apiVersion: string = 'v1'
    ) {
    }

    /**
     * Method for requesting data from a users by identifier.
     * @param {UserType} type User type.
     * @param {string} userId User identifier.
     * @returns { Promise<User>}
     */
    public async getUserById(type: UserType, userId: string): Promise<any> {
        if (type === UserType.ADMIN) {
            return axiosInstance.get(`${this.apiVersion}/admins/${userId}`)
                .then((response: AxiosResponse) => new User().fromJSON(response.data))
                .catch((err: any) => {
                    throw new Error(err.message)
                })
        }
        if (type === UserType.ANALYST) {
            return axiosInstance.get(`${this.apiVersion}/analysts/${userId}`)
                .then((response: AxiosResponse) => new User().fromJSON(response.data))
                .catch((err: any) => {
                    throw new Error(err.message)
                })
        }

    }

    public async getAdmins(): Promise<Array<any>> {
        return axiosInstance.get(`${this.apiVersion}/admins`)
            .then((response: AxiosResponse) => response.data)
            .catch((err: any) => {
                throw new Error(err.message)
            })
    }

    public async newAdmin(newUser: User): Promise<any> {
        try {
            return axiosInstance.post(`${this.apiVersion}/admins`,
                { ...newUser.toJSON() })
                .then((response: AxiosResponse) => response.data)
        } catch(err: any) {
            throw new Error(err.message)
        }
    }

    public async editAdmin(updateUser: User): Promise<any> {
        try {
            return axiosInstance.patch(`${this.apiVersion}/admins/${updateUser.id}`, { ...updateUser.toJSON() })
                .then((response: AxiosResponse) => response.data)
        } catch(err: any) {
            throw new Error(err.message)
        }
    }

    public async getAnalysts(): Promise<Array<any>> {
        return axiosInstance.get(`${this.apiVersion}/analysts`)
            .then((response: AxiosResponse) => response.data)
            .catch((err: any) => {
                throw new Error(err.message)
            })
    }

    public async newAnalysts(newUser: User): Promise<any> {
        try {
            return axiosInstance.post(`${this.apiVersion}/analysts`,
                { ...newUser.toJSON() })
                .then((response: AxiosResponse) => response.data)
        } catch(err: any) {
            throw new Error(err.message)
        }
    }

    public async editAnalysts(updateUser: User): Promise<any> {
        try {
            return axiosInstance.patch(`${this.apiVersion}/analysts/${updateUser.id}`, { ...updateUser.toJSON() })
                .then((response: AxiosResponse) => response.data)
        } catch(err: any)  {
            throw new Error(err.message)
        }
    }

    public async changeResources(userId: string, resources: Array<string>): Promise<void> {
        try {
            return axiosInstance.put(`${this.apiVersion}/analysts/${userId}/resources`, { resources })
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async newOrg(org: Organization): Promise<any> {
        try {
            return axiosInstance.post(`${this.apiVersion}/organizations`, { ...org.toJSON() })
                .then((response: AxiosResponse) => response.data)

        } catch(err: any) {
            throw new Error(err.message)
        }
    }

    public async updateOrg(org: Organization): Promise<any> {
        try {
            return axiosInstance.patch(`${this.apiVersion}/organizations/${org.id}`, { ...org.toJSON() })
                .then((response: AxiosResponse) => response.data)

        } catch(err: any) {
            throw new Error(err.message)
        }
    }

    public async getAll(): Promise<any> {
        return axiosInstance.get(`${this.apiVersion}/organizations`)
            .then((response: AxiosResponse) => response.data)
            .catch((err: any) => {
                throw new Error(err.message)
            })
    }

    public async getUsersByOrg(org_id: string): Promise<any> {
        return axiosInstance.get(`${this.apiVersion}/organizations/${org_id}/users`)
            .then((response: AxiosResponse) => response.data)
            .catch((err: any) => {
                throw new Error(err.message)
            })
    }

    public async orgResponsibles(user_id: string, org_id: string): Promise<any> {
        return axiosInstance.put(`${this.apiVersion}/organizations/${org_id}/responsibles/${user_id}`)
            .then((response: AxiosResponse) => response.data)
            .catch((err: any) => {
                throw new Error(err.message)
            })
    }

    public async userActive(user_id: string, active: boolean): Promise<any> {
        return axiosInstance.put(`${this.apiVersion}/users/${user_id}/active`, { active })
            .then((response: AxiosResponse) => response.data)
            .catch((err: any) => {
                throw new Error(err)
            })
    }

    public async actUpload(formData: FormData, orgId: string): Promise<void> {
        try {
            return axiosInstance.put(`${this.apiVersion}/organizations/${orgId}/act`,
                formData,
                {
                    headers: {
                        "Content-type": "multipart/form-data"
                    }
                })
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async actDownload(orgId: string): Promise<void> {
        try {
            return axiosInstance.get(`${this.apiVersion}/organizations/${orgId}/act`, {
                responseType: 'arraybuffer',
            })
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async signup(analyst: IActionSignup): Promise<void> {
        try {
            return axiosInstance.post(`${this.apiVersion}/analystrequests`, { ...analyst })
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async usersRequest(): Promise<void> {
        try {
            return axiosInstance.get(`${this.apiVersion}/analystrequests`)
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async deleteUserRequest(id: any) {
        try {
            return axiosInstance.delete(`${this.apiVersion}/analystrequests/${id}`)
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async findOneUserRequestr(id: string) {
        try {
            return axiosInstance.get(`${this.apiVersion}/analystrequests/${id}`)
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async findOrgNameRequest() {
        try {
            return axiosInstance.get(`${this.apiVersion}/organizations/names`)
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async ufRequest() {
        try {
            return axiosInstance.get(`${this.apiVersion}/ufs`)
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async logoUpload(formData: FormData, orgId: string): Promise<void> {
        try {
            return axiosInstance.put(`${this.apiVersion}/organizations/${orgId}/logo`,
                formData,
                {
                    headers: {
                        "Content-type": "multipart/form-data"
                    }
                })
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

    public async logsRequest(id: string): Promise<void> {
        try {
            return axiosInstance.get(`${this.apiVersion}/auditlogs?user.id=${id}`)
                .then((response: AxiosResponse) => response.data)
        } catch (err: any) {
            throw new Error(err.message)
        }
    }

}

const userService: UserService = new UserService()

export default userService
